import type { RouteRecordRaw } from "vue-router"
import LayoutSwitcher from "@core/layouts/LayoutSwitcher.vue"
import { getHomeRedirectTarget } from "@core/router/homeRedirect"
import { porteAlarmesEnergyManager } from "@core/router/alarmesEnergyManager"
import { useAuthStore } from "@core/stores/auth.store"
import { buildingShellRoute } from "@core/router/building.shell.merged"
import { sourceShellRoute } from "@core/router/source.routes"
import HomeRouteFallback from "@core/pages/HomeRouteFallback.vue"
import PageNotFound from "@core/pages/PageNotFound.vue"
import PageLogin from "@core/pages/auth/PageLogin.vue"
import PageAuthChangePassword from "@core/pages/auth/PageAuthChangePassword.vue"
import PageAuthValidationAccount from "@core/pages/auth/PageAuthValidationAccount.vue"
import PageSourceList from "@core/pages/source/PageSourceList.vue"
import PageBuildingAppend from "@core/pages/onboarding/PageBuildingAppend.vue"
import PageBuildingGroup from "@core/pages/onboarding/PageBuildingGroup.vue"
import PageBuildingAppendMultiMeters from "@core/pages/onboarding/PageBuildingAppendMultiMeters.vue"
import PageUserAlarm from "@core/pages/user/PageUserAlarm.vue"
import PageUserSettings from "@core/pages/user/PageUserSettings.vue"
import PageConsent from "@core/pages/consentements/PageConsent.vue"
import PageConsentSignInvitation from "@core/pages/consentrequest/PageConsentSignInvitation.vue"
import PageConsentShareInvitation from "@core/pages/consentrequest/PageConsentShareInvitation.vue"
import PageConsentExtendInvitation from "@core/pages/consentrequest/PageConsentExtendInvitation.vue"
import PageConsentChangeOwnerInvitation from "@core/pages/consentrequest/PageConsentChangeOwnerInvitation.vue"
import PageConsentSignMulti from "@core/pages/consentrequest/PageConsentSignMulti.vue"
import PageChangeSpace from "@core/pages/PageChangeSpace.vue"
import PageSubscriptionCancelled from "@core/pages/PageSubscriptionCancelled.vue"

// ====================================
// Module coeur
// ====================================
export const routes = [
    {
        path: "/",
        component: LayoutSwitcher,
        children: [
            {
                path: "",
                name: "Home",
                component: HomeRouteFallback,
                meta: {
                    requiresAuth: true,
                    layout: "header",
                },
                beforeEnter: async (to, _from, next) => {
                    const target = await getHomeRedirectTarget(to)
                    if (target === null) {
                        next()
                        return
                    }
                    next(target)
                },
            },
            buildingShellRoute,
            {
                path: "user/alarm",
                name: "PageUserAlarm",
                component: PageUserAlarm,
                meta: {
                    requiresAuth: true,
                    layout: "header-sidebar",
                    sidebar: "user",
                },
                // Paramètres des alarmes : réservés à l'offre Energy Management (DEV-2252)
                beforeEnter: async (_to, _from, next) => {
                    const verdict = await porteAlarmesEnergyManager(
                        useAuthStore(),
                        { name: "PageUserSettings" }
                    )
                    if (verdict === true) next()
                    else next(verdict)
                },
            },
            {
                path: "user/settings",
                name: "PageUserSettings",
                component: PageUserSettings,
                meta: {
                    requiresAuth: true,
                    layout: "header-sidebar",
                    sidebar: "user",
                },
            },
            {
                path: "source",
                name: "PageSourceList",
                component: PageSourceList,
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: "onboarding/building",
                name: "PageBuildingAppend",
                component: PageBuildingAppend,
                meta: {
                    requiresAuth: true,
                    layout: "header",
                },
            },
            {
                path: "onboarding/group-building",
                name: "PageBuildingGroup",
                component: PageBuildingGroup,
                meta: {
                    requiresAuth: true,
                    layout: "header",
                },
            },
            {
                path: "onboarding/append-multi-meters",
                name: "PageBuildingAppendMultiMeters",
                component: PageBuildingAppendMultiMeters,
                meta: {
                    requiresAuth: true,
                    layout: "header",
                },
            },
            sourceShellRoute,
            {
                path: "login",
                name: "PageLogin",
                component: PageLogin,
                meta: {
                    layout: "blank",
                },
            },
            {
                path: "user/:_id_user/changepassword",
                name: "PageAuthChangePassword",
                component: PageAuthChangePassword,
                meta: {
                    layout: "blank",
                },
            },
            {
                path: "user/:_id_user/validation",
                name: "PageAuthValidationAccount",
                component: PageAuthValidationAccount,
                meta: {
                    layout: "blank",
                },
            },
            {
                path: "change-space",
                name: "PageChangeSpace",
                component: PageChangeSpace,
                meta: {
                    requiresAuth: true,
                    layout: "blank",
                },
            },
            {
                path: "subscription-cancelled",
                name: "PageSubscriptionCancelled",
                component: PageSubscriptionCancelled,
                meta: {
                    requiresAuth: true,
                    layout: "blank",
                },
            },
            {
                path: "consentements",
                name: "PageConsent",
                component: PageConsent,
                meta: {
                    requiresAuth: true,
                    layout: "header-sidebar",
                    sidebar: "user",
                },
            },
            {
                path: "demande/signature-consentement/:id",
                name: "PageConsentSignInvitation",
                component: PageConsentSignInvitation,
                meta: {
                    layout: "blank",
                },
            },
            {
                path: "demande/partage-consentement/:id",
                name: "PageConsentShareInvitation",
                component: PageConsentShareInvitation,
                meta: {
                    layout: "blank",
                },
            },
            {
                path: "demande/prolongement-consentement/:id",
                name: "PageConsentExtendInvitation",
                component: PageConsentExtendInvitation,
                meta: {
                    layout: "blank",
                },
            },
            {
                path: "demande/changement-titulaire/:id",
                name: "PageConsentChangeOwnerInvitation",
                component: PageConsentChangeOwnerInvitation,
                meta: {
                    layout: "blank",
                },
            },
            {
                path: "demande/signature-multi-compteurs/:_id",
                name: "PageConsentSignMulti",
                component: PageConsentSignMulti,
                meta: {
                    layout: "blank",
                },
            },
            {
                // Liveness diag (DEV-427) — page lazy-loadée, non listée dans la
                // nav, gated par le BFF (token + service allowlist). Cible
                // exclusive : BetterStack Browser Monitor.
                path: "diag/alive/:service",
                name: "PageDiagAlive",
                component: () => import("@core/pages/diag/PageDiagAlive.vue"),
                meta: {
                    layout: "blank",
                    documentTitle: "Liveness",
                },
            },
            {
                path: ":pathMatch(.*)*",
                name: "PageNotFound",
                component: PageNotFound,
                meta: {
                    layout: "header",
                },
            },
        ],
    },
] as RouteRecordRaw[]
